<template>
  <Dialog v-model:visible="visible" header="Add API key" :style="{ width: '50rem' }" modal>
    <div class="flex items-center gap-4 mb-4">
      <label for="name" class="font-semibold w-20">Name</label>
      <InputText id="name" v-model="name" placeholder="Name" class="flex-auto" :disabled="!!key" />
    </div>
    <div v-if="key" class="flex flex-col gap-4 mb-4 mt-8">
      <div class="mt-2">
        <strong>Note:</strong> This is the only time you will see this key. Please copy and store it
        securely.
      </div>
      <InputText id="key" v-model="key" placeholder="Key" class="flex-auto" readonly />
    </div>
    <template #footer>
      <Button @click="visible = false" label="Cancel" severity="secondary" />
      <Button
        v-if="!key"
        @click="create"
        label="Add API key"
        :disabled="name.length === 0"
        :loading="pending"
      />
      <Button v-if="key" @click="visible = false" label="Close" />
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { useApiClient } from '@/api/apiClient';
import { usePromise } from '@/composables/usePromise';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import { ref, watch } from 'vue';
import * as Sentry from '@sentry/vue';
import Button from 'primevue/button';

const { api } = useApiClient();

const visible = defineModel<boolean>('visible', { required: true });

const emit = defineEmits<{
  (e: 'created'): void;
}>();

const name = ref('');
const key = ref('');

const {
  run: create,
  pending: pending,
  error: error,
} = usePromise(async () => {
  const { data, error } = await api.apiKeyControllerCreate({ body: { name: name.value } });
  if (error) {
    throw new Error('Failed to create API key');
  }
  key.value = data?.key ?? '';
  emit('created');
});

watch(error, () => {
  if (error.value) {
    console.error(error.value);
    Sentry.captureException(error.value);
  }
});
</script>
